/**
 * AiFindingsPanel — right-side panel listing AI-generated findings for a study.
 *
 * Scaffold: shows the findings with severity badges, confidence, accept/reject
 * buttons and a click-to-jump handler. Geometry overlays (bounding boxes, SEG
 * masks) are NOT rendered yet — that lands in a later session once we can see
 * real SR/SEG files from the PACS.
 */
import { useEffect, useState } from 'react';
import {
  AiFinding, FindingStatus, listAiFindings, setFindingStatus,
  severityRank, confidencePct,
} from '../utils/aiFindings';
import styles from './AiFindingsPanel.module.css';

interface Props {
  studyId: number | null;
  /** Click a finding → load its series and jump to the flagged slice. */
  onJump: (seriesId: number, imageIndex: number) => void;
}

export default function AiFindingsPanel({ studyId, onJump }: Props) {
  const [findings, setFindings] = useState<AiFinding[]>([]);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [filter, setFilter] = useState<FindingStatus | 'all'>('all');

  useEffect(() => {
    if (!studyId) { setFindings([]); return; }
    let cancelled = false;
    setLoading(true);
    listAiFindings(studyId).then((list) => {
      if (cancelled) return;
      list.sort((a, b) =>
        severityRank(a.severity) - severityRank(b.severity) ||
        b.confidence - a.confidence
      );
      setFindings(list);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [studyId]);

  async function setStatus(f: AiFinding, status: FindingStatus) {
    const ok = await setFindingStatus(f.findingId, status);
    if (ok) {
      setFindings((prev) => prev.map((x) =>
        x.findingId === f.findingId ? { ...x, status } : x));
    }
  }

  const visible = filter === 'all' ? findings : findings.filter((f) => f.status === filter);

  // Hidden when the study has no findings — no point showing an empty chrome.
  if (!loading && findings.length === 0) return null;

  if (collapsed) {
    return (
      <div className={`${styles.panel} ${styles.collapsed}`}>
        <button
          className={styles.toggleBtn}
          onClick={() => setCollapsed(false)}
          title={`AI findings (${findings.length})`}
        >AI</button>
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.title}>AI Findings ({findings.length})</span>
        <button className={styles.toggleBtn} onClick={() => setCollapsed(true)} title="Collapse">
          &#9656;
        </button>
      </div>

      <div className={styles.filters}>
        {(['all', 'pending', 'accepted', 'rejected'] as const).map((f) => (
          <button
            key={f}
            className={`${styles.filterBtn} ${filter === f ? styles.filterActive : ''}`}
            onClick={() => setFilter(f)}
          >
            {f}
          </button>
        ))}
      </div>

      <div className={styles.list}>
        {loading && <div className={styles.empty}>Loading…</div>}
        {!loading && visible.length === 0 && (
          <div className={styles.empty}>No {filter === 'all' ? '' : filter} findings.</div>
        )}
        {visible.map((f) => (
          <FindingItem
            key={f.findingId}
            finding={f}
            onAccept={() => setStatus(f, 'accepted')}
            onReject={() => setStatus(f, 'rejected')}
            onJump={() => {
              if (f.seriesId != null && f.imageIndex != null) onJump(f.seriesId, f.imageIndex);
            }}
          />
        ))}
      </div>
    </div>
  );
}

interface ItemProps {
  finding: AiFinding;
  onAccept: () => void;
  onReject: () => void;
  onJump: () => void;
}

function FindingItem({ finding, onAccept, onReject, onJump }: ItemProps) {
  const canJump = finding.seriesId != null && finding.imageIndex != null;
  return (
    <div className={`${styles.item} ${styles[`severity_${finding.severity}`] ?? ''}`}>
      <div className={styles.itemHeader}>
        <span className={styles.severity}>{finding.severity}</span>
        <span className={styles.confidence}>{confidencePct(finding.confidence)}</span>
      </div>
      <button
        className={styles.findingBtn}
        onClick={onJump}
        disabled={!canJump}
        title={canJump ? 'Jump to image' : 'No image reference'}
      >
        {finding.finding || finding.code || 'Unlabeled finding'}
      </button>
      <div className={styles.vendor}>
        {finding.vendor}{finding.model ? ` · ${finding.model}` : ''}
      </div>
      <div className={styles.actions}>
        <button
          className={`${styles.actBtn} ${finding.status === 'accepted' ? styles.actAccepted : ''}`}
          onClick={onAccept}
        >Accept</button>
        <button
          className={`${styles.actBtn} ${finding.status === 'rejected' ? styles.actRejected : ''}`}
          onClick={onReject}
        >Reject</button>
      </div>
    </div>
  );
}
